import { useState, useCallback, useEffect, useContext, createContext, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as appActions from 'Actions/app';
import { MainAppReducer } from 'Types';
import { sliderToTempo, tempoToSlider } from 'Utils/tempoSliderMapping';
import { NotifyPhaserEvents } from 'Phaser/EventBus2';
import { defaultAtaSettings } from 'Reducers/mainAppReducer';

type ChangeTempoParams = {
  value: number;
  goal?: boolean;
  isSliderValue?: boolean;
  fromAta?: boolean;
};

interface CustomAtaThreshold {
  incThreshold: number;
  decThreshold: number;
  incAmount: number;
  decAmount: number;
  setIncThreshold: React.Dispatch<React.SetStateAction<number>>;
  setDecThreshold: React.Dispatch<React.SetStateAction<number>>;
  setIncAmt: React.Dispatch<React.SetStateAction<number>>;
  setDecAmt: React.Dispatch<React.SetStateAction<number>>;
}

export interface TempoContextType {
  max_tempo_value: number;
  min_tempo_value: number;
  tempo: number;
  performanceTempo: number;
  sliderValueTempo: number;
  sliderValueGoal: number;
  setSliderValueTempo: React.Dispatch<React.SetStateAction<number>>;
  setSliderValueGoal: React.Dispatch<React.SetStateAction<number>>;
  changeTempo: (params: ChangeTempoParams) => void;
  resetTemp: () => void;
  restoreSavedTempo: () => void;
  manualTempoInput: string;
  inAdaptiveSegment: boolean;
  ataEnabled: boolean;
  startAdaptiveSegment: (target: number) => void;
  endAdaptiveSegment: (finalTempo: number) => void;
  goalTempo: number;
  usingCustomGoalTempo: boolean;
  freePlayMode: boolean;
  setFreePlayMode: (freePlay: boolean) => void;
  customAtaThreshold: CustomAtaThreshold;
  //effectiveGoalTempo: number;
}

const TempoContext = createContext<TempoContextType | undefined>(undefined);

type TempoProviderProps = {
  performanceTempo: number;
  initialTempo: number;
  initialGoalTempo: number;
  parentContext: 'repertoire' | 'lesson';
  saveCurrentTempo: (tempo: number) => void;
  saveGoalTempo: (tempo: number) => void;
  /** Clear debounced saves and persist reset BPMs (e.g. Redux) so stale timers cannot overwrite reset. */
  onTempoResetPersistence?: (values: { currentTempo: number; goalTempo: number }) => void;
  children: React.ReactNode;
};

export const TempoProvider: React.FC<TempoProviderProps> = ({
  performanceTempo,
  initialTempo,
  initialGoalTempo,
  parentContext: _,
  saveCurrentTempo,
  saveGoalTempo,
  onTempoResetPersistence,
  children,
}: TempoProviderProps) => {
  const data = useSelector((state: MainAppReducer) => state.mainAppReducer);
  const ataSettings = data.ataSettings ?? defaultAtaSettings;

  const ataEnabled = ataSettings.ataEnabled;
  const usingCustomGoalTempo = ataSettings.usingCustomGoalTempo;

  //const dispatch = useDispatch();

  const min_tempo_value = 40;
  const max_tempo_value = 300;
  const [tempo, setTempo] = useState(initialTempo);
  const [sliderValueTempo, setSliderValueTempo] = useState(
    () => tempoToSlider(initialTempo, min_tempo_value, performanceTempo, max_tempo_value) * 100,
  );
  const [sliderValueGoal, setSliderValueGoal] = useState(
    () => tempoToSlider(initialGoalTempo, min_tempo_value, performanceTempo, max_tempo_value) * 100,
  );
  const [manualTempoInput, setManualTempoInput] = useState(initialTempo.toString());
  const [inAdaptiveSegment, setInAdaptiveSegment] = useState(false);
  const [tempoRestorePoint, setTempoRestorePoint] = useState(initialTempo ?? 72);
  const [goalTempo, setGoalTempo] = useState(initialGoalTempo ?? 150);
  const [freePlayMode, setFreePlayMode] = useState(false);

  const [customIncThreshold, setCustomIncThreshold] = useState<number>(
    ataSettings.customIncThreshold ?? defaultAtaSettings.customIncThreshold,
  );
  const [customDecThreshold, setCustomDecThreshold] = useState<number>(
    ataSettings.customDecThreshold ?? defaultAtaSettings.customDecThreshold,
  );
  const [customIncAmt, setCustomIncAmt] = useState<number>(
    ataSettings.customIncAmt ?? defaultAtaSettings.customIncAmt,
  );
  const [customDecAmt, setCustomDecAmt] = useState<number>(
    ataSettings.customDecAmt ?? defaultAtaSettings.customDecAmt,
  );
  //const [effectiveGoalTempo, setEffectiveGoalTempo] = useState(performanceTempo);
  //

  const dispatch = useDispatch();

  const startAdaptiveSegment = useCallback(
    (base: number) => {
      setTempoRestorePoint(base);
      console.log('[tempo sync] startAdaptiveSegment', base);
      setInAdaptiveSegment(true);
    },
    [setInAdaptiveSegment],
  );

  const mapTempoToSlider = useCallback(
    (newTempo: number, goal: boolean) => {
      const normalised = tempoToSlider(
        newTempo,
        min_tempo_value,
        performanceTempo,
        max_tempo_value,
      );
      const sliderValue = normalised * 100;
      if (goal) setSliderValueGoal(sliderValue);
      else setSliderValueTempo(sliderValue);
    },
    [min_tempo_value, max_tempo_value, performanceTempo],
  );

  /*const debounceTimer = useRef<NodeJS.Timeout | null>(null);

  const saveGoalTempo = useCallback(
    (tempo: number) => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
      debounceTimer.current = setTimeout(() => {
        dispatch(appActions.setCustomGoalTempo(tempo));
        debounceTimer.current = null;
      }, 500);
    },
    [dispatch],
  );*/

  const changeTempo = useCallback(
    ({ value, goal, isSliderValue, fromAta }: ChangeTempoParams) => {
      if (isSliderValue)
        value = Math.round(
          sliderToTempo(value, min_tempo_value, performanceTempo, max_tempo_value),
        );
      value = Math.max(min_tempo_value, Math.min(max_tempo_value, value));
      if (goal) {
        setGoalTempo(value);
        mapTempoToSlider(value, true);
        saveGoalTempo(value);
      } else {
        setTempo(value);
        mapTempoToSlider(value, goal ?? false);
        setManualTempoInput(Math.round(value).toString());
        if (!fromAta) {
          if (value > goalTempo) {
            const nextGoalTempo = Math.min(value + 20, max_tempo_value);
            setGoalTempo(nextGoalTempo);
            mapTempoToSlider(nextGoalTempo, true);
          }
          if (inAdaptiveSegment) {
            NotifyPhaserEvents.AbortAdaptiveTempo({ tempoRestorePoint: value });
            setInAdaptiveSegment(false);
          }
          saveCurrentTempo(value);
          setTempoRestorePoint(value);
        }
      }
    },
    [
      mapTempoToSlider,
      min_tempo_value,
      max_tempo_value,
      performanceTempo,
      inAdaptiveSegment,
      goalTempo,
      saveCurrentTempo,
      saveGoalTempo,
    ],
  );

  useEffect(() => {
    console.log('[tempo sync] initialTempo changed', initialTempo);
  }, [initialTempo]);

  useEffect(() => {
    console.debug('[tempo sync] saveCurrentTempo changed', saveCurrentTempo);
  }, [saveCurrentTempo]);

  const endAdaptiveSegment = useCallback(
    (finalTempo: number) => {
      console.log('[tempo sync] endAdaptiveSegment', finalTempo);
      setTempoRestorePoint(finalTempo);
      changeTempo({ value: finalTempo });
      setInAdaptiveSegment(false);
    },
    [changeTempo],
  );

  useEffect(() => {
    console.log('[tempo sync] tempoRestorePoint changed', tempoRestorePoint);
  }, [tempoRestorePoint]);

  useEffect(() => {
    setTempo(initialTempo);
    setSliderValueTempo(
      tempoToSlider(initialTempo, min_tempo_value, performanceTempo, max_tempo_value) * 100,
    );
    setInAdaptiveSegment(false);
    setManualTempoInput(initialTempo.toString());
  }, [initialTempo]);

  useEffect(() => {
    setGoalTempo(initialGoalTempo);
    setSliderValueGoal(
      tempoToSlider(initialGoalTempo, min_tempo_value, performanceTempo, max_tempo_value) * 100,
    );
  }, [initialGoalTempo]);

  const defaultResetGoalTempo = 150;

  useEffect(() => {
    setTempoRestorePoint(initialTempo);
  }, [initialTempo]);

  const restoreSavedTempo = useCallback(() => {
    setTempo(tempoRestorePoint);
    console.log('[tempo sync] restoreSavedTempo', tempoRestorePoint);
    //setSliderValueTempo(tempoToSlider(tempoRestorePoint, min_tempo_value, performanceTempo, max_tempo_value) * 100);
    mapTempoToSlider(tempoRestorePoint, false);
    setManualTempoInput(Math.round(tempoRestorePoint).toString());
  }, [tempoRestorePoint, mapTempoToSlider]);

  const resetTemp = useCallback(() => {
    onTempoResetPersistence?.({
      currentTempo: performanceTempo,
      goalTempo: defaultResetGoalTempo,
    });
    setTempo(performanceTempo);
    setGoalTempo(defaultResetGoalTempo);
    setSliderValueTempo(50);
    mapTempoToSlider(defaultResetGoalTempo, true);
    setManualTempoInput(Math.round(performanceTempo).toString());
  }, [performanceTempo, mapTempoToSlider, onTempoResetPersistence]);

  const customAtaThreshold = {
    decAmount: customDecAmt,
    incAmount: customIncAmt,
    decThreshold: customDecThreshold,
    incThreshold: customIncThreshold,
    setIncThreshold: setCustomIncThreshold,
    setDecThreshold: setCustomDecThreshold,
    setIncAmt: setCustomIncAmt,
    setDecAmt: setCustomDecAmt,
  };

  useEffect(() => {
    console.debug('goal ', freePlayMode, usingCustomGoalTempo, goalTempo);
    const effectiveGoalTempo = freePlayMode ? (usingCustomGoalTempo ? goalTempo : 300) : 72;
    //setEffectiveGoalTempo(effectiveGoalTempo);
    NotifyPhaserEvents.UpdateGoalTempo({ goalTempo: effectiveGoalTempo });
  }, [usingCustomGoalTempo, freePlayMode, goalTempo]);

  useEffect(() => {
    dispatch(appActions.setCustomAtaSettings({ customDecAmt }));
  }, [customDecAmt]);

  useEffect(() => {
    dispatch(appActions.setCustomAtaSettings({ customIncAmt }));
  }, [customIncAmt]);

  useEffect(() => {
    dispatch(appActions.setCustomAtaSettings({ customIncThreshold }));
  }, [customIncThreshold]);

  useEffect(() => {
    dispatch(appActions.setCustomAtaSettings({ customDecThreshold }));
  }, [customDecThreshold]);

  /*useEffect(() => {
    dispatch(appActions.setCustomGoalTempo(goalTempo));
  }, [goalTempo]);*/

  return (
    <TempoContext.Provider
      value={{
        tempo,
        changeTempo,
        performanceTempo,
        sliderValueTempo,
        sliderValueGoal,
        setSliderValueTempo,
        setSliderValueGoal,
        resetTemp,
        restoreSavedTempo,
        manualTempoInput,
        max_tempo_value,
        min_tempo_value,
        inAdaptiveSegment,
        startAdaptiveSegment,
        endAdaptiveSegment,
        goalTempo,
        ataEnabled,
        usingCustomGoalTempo,
        freePlayMode,
        setFreePlayMode,
        customAtaThreshold,
        //effectiveGoalTempo,
      }}
    >
      {children}
    </TempoContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useTempoContext = (): TempoContextType => {
  const context = useContext(TempoContext);
  if (!context) {
    throw new Error('useTempoContext must be used within a TempoProvider');
  }
  return context;
};
